// Split out of src/components/Onboarding.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only).
// Onboarding.jsx re-exports everything it exported before, so existing imports keep working.

import { Icon } from "../Common.jsx";
import { parseJoinPayload, startQrScanner } from "../../lib/qrscan.js";
import { t as ct } from "../../lib/content.js";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// Full-screen camera view for the Join tab's in-app QR scanner (Aug 2026). Owns the whole
// camera lifecycle: starts it on mount via startQrScanner (qrscan.js), and tears it down
// on unmount, on ✕, and immediately on a successful scan — the stream must NEVER outlive
// this overlay. Decoded text that isn't a Little Bankers join payload shows a "keep
// looking" hint and scanning simply continues; only a well-formed payload calls onScanned
// (exactly once — guarded by handledRef, since the decode loop can fire the same QR on
// several consecutive frames).
function QrScanOverlay({ onScanned, onClose }) {
  const videoRef = useRef(null);
  const [fail, setFail] = useState(null); // null | "denied" | "unavailable"
  const [wrongQr, setWrongQr] = useState(false);
  const handledRef = useRef(false);
  // Callbacks reach the decode loop via a ref so the mount effect can run exactly once —
  // re-running it on every parent render would restart the camera each time.
  const onScannedRef = useRef(onScanned);
  onScannedRef.current = onScanned;

  useEffect(() => {
    let stop = null;
    let cancelled = false;
    (async () => {
      try {
        stop = await startQrScanner(videoRef.current, (text) => {
          if (handledRef.current) return;
          const payload = parseJoinPayload(text);
          if (!payload) {
            setWrongQr(true);
            return;
          }
          handledRef.current = true;
          onScannedRef.current(payload);
        });
        // If the overlay was closed while getUserMedia's permission prompt was still up,
        // the camera has just started for an unmounted view — stop it right away.
        if (cancelled && stop) stop();
      } catch (err) {
        if (!cancelled) setFail(err && err.kind === "denied" ? "denied" : "unavailable");
      }
    })();
    return () => {
      cancelled = true;
      if (stop) stop();
    };
  }, []);

  if (fail) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900/95 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg border border-slate-200 p-6 w-full max-w-sm md:max-w-[560px] text-center">
          <div className="text-lg font-extrabold text-slate-900 mb-2">
            {fail === "denied" ? ct("onboarding.qrscan.denied_heading", "Camera access was blocked") : ct("onboarding.qrscan.unavailable_heading", "Camera isn't available")}
          </div>
          <p className="text-sm text-slate-500 mb-4">
            {fail === "denied"
              ? ct("onboarding.qrscan.denied_body", "You can allow camera access in your browser settings — or just enter the code and passcode manually instead.")
              : ct("onboarding.qrscan.unavailable_body", "This device's camera couldn't be started — enter the code and passcode manually instead.")}
          </p>
          <button className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5" onClick={onClose}>{ct("onboarding.qrscan.back_button", "Back to Join")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900 flex flex-col" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}>
      <div className="flex items-center justify-between px-4 pb-3">
        <div className="text-white font-bold text-sm">{ct("onboarding.qrscan.title", "Scan the household QR")}</div>
        <button aria-label="Close scanner" className="text-white/80 font-bold text-xl px-2 leading-none" onClick={onClose}><Icon id="ic-close" size={20} style={{ color: "currentColor" }} /></button>
      </div>
      <div className="flex-1 relative overflow-hidden">
        <video ref={videoRef} muted playsInline className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-56 h-56 rounded-2xl border-2 border-white/80" />
        </div>
        {wrongQr && (
          <div className="absolute bottom-8 left-0 right-0 flex justify-center px-4">
            <div className="bg-rose-600/90 text-white text-xs font-bold rounded-lg px-3 py-2">{ct("onboarding.qrscan.wrong_code", "That's not a Little Bankers join code. Keep looking.")}</div>
          </div>
        )}
      </div>
      <p className="text-white/70 text-xs font-semibold text-center px-6 pt-4" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}>
        {ct("onboarding.qrscan.instruction", "Point at the QR on the other device — Parent Mode → Settings → Household → Quick join")}
      </p>
    </div>
  );
}

export { QrScanOverlay };
